import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { Drawer, Layout, Tabs, Tag, Text } from '@loadsmart/miranda-react'

import { RequestStatus } from 'core/domain/Request'
import { formatMode } from 'core/presenters/modePresenter'
import { LoadingState } from 'core/ui/async-content/LoadingState'
import { formatDate } from 'core/utils/date'

import { useShipperDetail } from '../hooks/useShipperDetail'
import {
  formatShipperAddress,
  formatShipperLocation,
  mapShipperCountryToCountryFlag,
} from '../presenters/shipperPresenter'

export type ShipperDetailDrawerProps = {
  shipperUUID: string | undefined
  isOpen: boolean
  onClose: () => void
}

const shipperDetailTabs = {
  details: {
    value: 'details',
    label: 'Details',
  },
} as const

type ShipperDetailTab = (typeof shipperDetailTabs)[keyof typeof shipperDetailTabs]['value']

export function ShipperDetailDrawer({ shipperUUID, isOpen, onClose }: ShipperDetailDrawerProps) {
  const { shipperDetail, status } = useShipperDetail(shipperUUID)
  const [activeTab, setActiveTab] = useState<ShipperDetailTab>(shipperDetailTabs.details.value)

  const isLoading = status === RequestStatus.Pending || status === RequestStatus.Initial

  useEffect(() => {
    if (status === RequestStatus.Error) {
      onClose()
      toast.error('Failed to load shipper details')
    }
  }, [status, onClose])

  if (!isOpen) return null

  return (
    <Drawer
      open={isOpen}
      onClose={(event: CustomEvent) => {
        if (event.target === event.currentTarget) return
        onClose()
      }}
      size="medium"
    >
      <Drawer.Header>
        <Text variant="heading-md-bold">Shipper</Text>
        <Drawer.Close />
      </Drawer.Header>

      <Drawer.Body>
        {isLoading && (
          <Layout.Box padding="spacing-6">
            <LoadingState />
          </Layout.Box>
        )}

        {status === RequestStatus.Done && shipperDetail && (
          <Layout.Stack gap="spacing-4">
            <Layout.Box
              backgroundColor="color-background-primary"
              borderColor="color-border"
              borderWidth="border-thin"
              borderRadius="border-radius-s"
              padding="spacing-4"
            >
              <Layout.Stack gap="spacing-3">
                <Text variant="heading-md-bold">{shipperDetail.companyName}</Text>
                <Layout.Stack gap="spacing-1">
                  <Text variant="body-md" color="color-text-secondary">
                    {formatShipperAddress(shipperDetail)}
                  </Text>
                  <Layout.Group gap="spacing-2" align="center">
                    <Text variant="body-md" color="color-text-secondary">
                      {formatShipperLocation(shipperDetail)}
                    </Text>
                    {shipperDetail.country && (
                      <Layout.Group gap="spacing-1" align="center">
                        {mapShipperCountryToCountryFlag(shipperDetail.country)}
                        <Text variant="body-md" color="color-text-secondary">
                          {shipperDetail.country}
                        </Text>
                      </Layout.Group>
                    )}
                  </Layout.Group>
                </Layout.Stack>
                {shipperDetail.modes.length > 0 && (
                  <Layout.Group gap="spacing-2">
                    {shipperDetail.modes.map(mode => (
                      <Tag key={mode} size="small" variant="neutral">
                        {formatMode(mode)}
                      </Tag>
                    ))}
                  </Layout.Group>
                )}
              </Layout.Stack>
            </Layout.Box>

            <Layout.Box
              backgroundColor="color-background-primary"
              borderColor="color-border"
              borderWidth="border-thin"
              borderRadius="border-radius-s"
              padding="spacing-4"
            >
              <Layout.Stack gap="spacing-3">
                <Text variant="heading-sm-bold">Overview</Text>
                <Tabs
                  activeTab={activeTab}
                  onTabChange={event => {
                    setActiveTab(event.detail.value as ShipperDetailTab)
                  }}
                >
                  <Tabs.Tab name={shipperDetailTabs.details.value}>
                    {shipperDetailTabs.details.label}
                  </Tabs.Tab>
                  <Tabs.Panel forTab={shipperDetailTabs.details.value}>
                    <Layout.Box
                      backgroundColor="color-background-primary"
                      borderColor="color-border"
                      borderWidth="border-thin"
                      borderRadius="border-radius-s"
                      padding="spacing-4"
                    >
                      <Layout.Group gap="spacing-4" align="center">
                        <Text variant="body-md" color="color-text-tertiary">
                          Date added
                        </Text>
                        <Text variant="body-md" color="color-text-primary">
                          {formatDate(shipperDetail.carrierAddedAt, 'MMM dd, yyyy')}
                        </Text>
                      </Layout.Group>
                    </Layout.Box>
                  </Tabs.Panel>
                </Tabs>
              </Layout.Stack>
            </Layout.Box>
          </Layout.Stack>
        )}
      </Drawer.Body>
    </Drawer>
  )
}
